import { useCallback, useEffect, useRef, useState } from 'react';

import { useIntersect } from '@common/hooks';
import { useCatalogProductIdParams, usePdpBaseInfo, usePdpConfig } from '@domains/pdp-common';

import {
  DESCRIPTION_CLASS_NAME,
  extraStyleShopCssInfo,
  instagramApi,
  whiteStyleShopCssInfo,
} from '../constants/description.constant';
import { ProductDescriptionContainerProps } from '../containers/DescriptionContainer';
import { defaultCss } from '../css/shop-css';
import { useExtraField } from '../hooks/useExtraField';
import { useInitZoom } from '../hooks/useInitZoom';
import { useLazyLoadImages } from '../hooks/useLazyLoadImages';
import { useParseDescription } from '../hooks/useParseDescription';
import { useDescriptionUBL } from '../log/useDescriptionUBL';
import { convertAnchorHref, convertZigzagOriginImage } from '../utils/html-convert';

declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

interface ProductDescriptionServiceProps extends ProductDescriptionContainerProps {}

export const useProductDescriptionService = ({
  isDescriptionOpened,
  setIsDescriptionOpened,
  intersectionObserverCallback,
  options,
}: ProductDescriptionServiceProps) => {
  const { isZigzagApp } = usePdpConfig();
  const { zoomRef, panningDisabled, onZoomStop } = useInitZoom();
  const { clickDescToggleButtonUBL } = useDescriptionUBL();
  const params = useCatalogProductIdParams();
  const {
    product,
    product: { crema_fit_url_list },
    shop,
  } = usePdpBaseInfo(params);
  const { top_description_key_list, bottom_description_key_list } = useExtraField(shop.id);
  const productDescription = product.description ?? '';
  const hasInstagramPost = productDescription.indexOf('data-instgrm-permalink') > -1;
  const isZbrowser = Boolean(product.external_product_url);
  const whiteStyleShopCss = whiteStyleShopCssInfo[shop.id];
  const extraStyleShopCss = extraStyleShopCssInfo[shop.id];
  const { parseDescription } = useParseDescription({
    shopId: shop.id,
    productId: product.id,
    shop_url: shop.url,
    description: productDescription,
  });
  // MEMO: 네이티브 PDP일 경우 본문은 프리패치되어 렌더링되어있는데 상단 영역 호출은 미랜더된 상황에서 본문 내 옵저버 인터페이스가 호출되므로 최초 스크롤 이후에 호출되게 하기 위해 최초 스크롤 여부 저장
  const [hasScrolled, setHasScrolled] = useState(false);
  const [lazyImages, setLazyImages] = useState<HTMLImageElement[]>([]);
  const [description, setDescription] = useState<string | JSX.Element | JSX.Element[]>('');

  const intersectionObserver = useCallback(
    (entry) => {
      intersectionObserverCallback?.(entry);
    },
    [intersectionObserverCallback],
  );
  const { targetRef: productVisibleTargetRef } = useIntersect(intersectionObserver, options);

  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const productVisibleIntersectRef = hasScrolled && intersectionObserverCallback ? productVisibleTargetRef : null;

  const handleDescToggleButtonClick = () => {
    if (!isDescriptionOpened) {
      clickDescToggleButtonUBL(product.id);
    }
    setIsDescriptionOpened(!isDescriptionOpened);
  };

  useLazyLoadImages({
    lazyImages,
  });

  useEffect(() => {
    const initInstagram = () => {
      // MEMO: 앱에서 탭 전환 시 재로딩 실패하는 이슈 때문에 딜레이 설정
      setTimeout(() => {
        window.instgrm?.Embeds.process();
      }, 100);
    };

    initInstagram();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setHasScrolled(true);
      window.removeEventListener('scroll', onScroll);
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDescription(parseDescription);
  }, [parseDescription]);

  useEffect(() => {
    const ref = descriptionRef.current;
    if (!ref) {
      return;
    }

    if (isZigzagApp) {
      const filteredLinks = `.${DESCRIPTION_CLASS_NAME} a:not([href^="zigzag://"]`;
      const anchorTags = Array.from(ref.querySelectorAll(filteredLinks)) as HTMLAnchorElement[];
      convertAnchorHref(anchorTags);
    }
    const imgTags = Array.from(ref.querySelectorAll(`img[data-origin^="zigzag://"]`)) as HTMLImageElement[];
    convertZigzagOriginImage(imgTags);

    const lazyImageTags = Array.from(ref.querySelectorAll('img.lazy')) as HTMLImageElement[];
    setLazyImages(lazyImageTags);
  }, [description, descriptionRef.current]);

  useEffect(() => {
    if (hasInstagramPost) {
      const script = document.createElement('script');
      script.src = instagramApi;

      document.body.appendChild(script);

      return () => {
        script.remove();
      };
    }
  }, [hasInstagramPost]);

  return {
    zoomRef,
    descriptionRef,
    productVisibleIntersectRef,
    style: {
      inlineStyle: whiteStyleShopCss,
      extraStyle: extraStyleShopCss,
      defaultStyle: whiteStyleShopCss ? null : defaultCss,
    },
    content: {
      description,
      top_description_key_list,
      bottom_description_key_list,
      extra_description_list: product.extra_description_list,
      purchase_notice: product.purchase_notice,
      crema_fit_url_list,
    },
    display: {
      isZbrowser,
      panningDisabled,
    },
    action: {
      handleDescToggleButtonClick,
      onZoomStop,
      customTransform: (x: number, y: number, scale: number) =>
        scale === 1 ? '' : `translate(${x}px, ${y}px) scale(${scale}) translateZ(0px)`,
    },
  };
};
